import {
  Box, Typography, TextField, Button, Grid, Stack, MenuItem, InputLabel, Select,
  FormControl, Paper, IconButton, FormHelperText, Tooltip, FormControlLabel,
  Switch,
  InputAdornment, Pagination, Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useState, useEffect } from 'react';
import {
  Visibility,
  VisibilityOff,
  ContentCopy
  , Edit, DeleteOutline
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RightDrawer from '../components/RightDrawer';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import { CircularProgress } from '@mui/material';
import { Chip } from '@mui/material';
import { Add, DownloadOutlined } from '@mui/icons-material'
import { Alert, Snackbar } from '@mui/material';
import { DELETE_DATA, GET_LISTBYID, UPDATE, SAVE, GET_LIST, GET_FILE, UPDATE2 } from '../utils/api';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const getStatusChip = (status) => {
  const normalized = status?.toLowerCase();

  switch (normalized) {
    case 'vendor':
      return (
        <Chip
          label="Vendor"
          size="small"
          sx={{
            color: '#2e7d32',
            backgroundColor: '#e8f5e9',
            border: '1px solid #66BB6A',
            fontWeight: 'bold',
          }}
        />
      );
    case 'admin':
      return (
        <Chip
          label="Admin"
          size="small"
          sx={{
            color: '#6a1b9a',
            backgroundColor: '#f3e5f5',
            border: '1px solid #AB47BC',
            fontWeight: 'bold',
          }}
        />
      );
    default:
      return (
        <Chip
          label={status}
          size="small"
          sx={{
            color: 'gray',
            backgroundColor: '#f0f0f0',
            fontWeight: 'bold',
          }}
        />
      );
  }
};

const User = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('user_name');
  const [order, setOrder] = useState('asc');
  const [formData, setFormData] = useState({
    id: '',
    user_name: '',
    email: '',
    role: '',
    password: '',
    confirmPassword: '',
    isPasswordAutoGenerated: false,
    vendorLegalName: '',
    dbaName: '',
    website: '',
    vendorType: '',
    ownershipType: '',
    taxId: '',
    contactFirstName: '',
    contactEmail: '',
    contactPhone: '',
    zip: '',
    country: '',
    vendorStatus: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [alert, setAlert] = useState({ type: '', message: '', open: false });
  const [editId, setEditId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const editMode = Boolean(formData.id);

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);


  const handleOpenDrawer = () => setDrawerOpen(true);
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setFormData({ user_name: '', email: '', role: '', password: '', confirmPassword: '' });
    setFormErrors({});
    setEditId(null); // Optional: if you're using edit mode
  };


  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const baseURL = process.env.REACT_APP_API_BASE_URL_ADMIN;
  const token = localStorage.getItem('token');

  // Fetch users from API
  const fetchUsers = async (searchText = '') => {
    setLoading(true);
    try {
      let queryParams = {
        ...(searchText ? { searchText } : {}),
        limit: limit
      };
      const data = await GET_LIST('/users', queryParams);
      if (data.statusCode == 401) {
        logout();
        navigate('/login');
      }
      setUsers(data?.data || []);
      setTotalPages(data?.data.totalPages)
      setError(null);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const requiredFields = [
    'vendorLegalName',
    'vendorType',
    'ownershipType',
    'website',
    'taxId',
    'contactEmail',
    'contactPhone',
    'zip',
    'country',
    'vendorStatus',
  ];

  const validateAll = () => {
    const newErrors = {};
    const vendorErrors = {};

    // User validation
    if (!formData.user_name) newErrors.user_name = 'User name is required';
    if (!editId && !formData.email) newErrors.email = 'Email is required';
    if (!formData.role) newErrors.role = 'Role is required';
    if (!editId && !formData.password) newErrors.password = 'Password is required';

    // Vendor-specific validation if role is vendor
    if (formData.role === 'vendor') {
      requiredFields.forEach((field) => {
        if (!formData[field] || formData[field].toString().trim() === '') {
          vendorErrors[field] = 'This field is required';
        }
      });

      // Additional vendor-specific validations
      if (formData.contactPhone && formData.contactPhone.length !== 10) {
        vendorErrors.contactPhone = 'Phone number must be exactly 10 digits';
      }
      if (formData.taxId && formData.taxId.length < 9) {
        vendorErrors.taxId = 'Tax ID must be at least 9 characters';
      }
    }

    // Set errors to respective states
    setFormErrors(newErrors);
    setErrors(vendorErrors);

    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(vendorErrors).length === 0
    );
  };

  const handleSaveUser = async () => {
    if (!validateAll()) return;

    if (!formData.isPasswordAutoGenerated && !editId && !strongPasswordRegex.test(formData.password)) {
      setFormErrors(prev => ({
        ...prev,
        password:
          'Password must be 6–10 characters long, with uppercase, lowercase, number, and special character.',
      }));
      return;
    }

    const payload = {
      user_name: formData.user_name,
      email: formData.email,
      role: formData.role,
      isPasswordAutoGenerated: formData.isPasswordAutoGenerated,
      ...(formData.role === 'vendor' && {
        vendorDetails: {
          vendor_name: formData.vendorLegalName,
          dba_name: formData.dbaName,
          business_website: formData.website,
          vendor_type: formData.vendorType,
          ownership_type: formData.ownershipType,
          tax_id: formData.taxId,
          contact_name: formData.contactFirstName,
          contact_email: formData.contactEmail,
          contact_phone: formData.contactPhone,
          zip_code: formData.zip,
          country: formData.country,
          active: formData.vendorStatus === 'active',
          additional_notes: formData.notes,
        }
      }),
      password: formData.password,
    };

    const payload2 = {
      user_name: formData.user_name,
      email: formData.email,
      role: formData.role,
    };

    try {
      let response;
      if (editId) {
        response = await UPDATE(`/users/${editId}`, payload2);
      } else {
        response = await SAVE('/users', payload);
      }

      if (response.statusCode === 401) {
        logout();
        navigate('/login');
      }

      if (response?.status === 200 || response?.status === 201) {
        setAlert({
          type: 'success',
          message: editId ? 'User updated successfully!' : 'User added successfully!',
          open: true,
        });
        fetchUsers(searchText);
        setDrawerOpen(false);
        setFormData({ user_name: '', email: '', role: '', password: '', confirmPassword: '' });
        setFormData(prev => ({
          ...prev,
          vendorLegalName: '',
          dbaName: '',
          website: '',
          vendorType: '',
          ownershipType: '',
          taxId: '',
          contactFirstName: '',
          contactLastName: '',
          jobTitle: '',
          contactEmail: '',
          contactPhone: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          country: '',
          vendorStatus: '',
          notes: '',
        }));
        setFormErrors({});
        setEditId(null);
      } else {
        setAlert({
          type: 'error',
          message: response?.data.message || `Failed with status ${response?.status}`,
          open: true,
        });
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setAlert({
        type: 'error',
        message: 'Something went wrong while saving the user.',
        open: true,
      });
    }
  };

  const handleEditClick = async (id) => {
    try {
      const response = await GET_LISTBYID('/users', id);
      const data = response;

      setEditId(id); // Set edit mode

      setFormData({
        user_name: data.user_name || '',
        email: data.email || '',
        role: data.role || '',
        password: '', // Keep password blank for editing,
      });

      setDrawerOpen(true);
    } catch (error) {
      console.error('Failed to fetch user for edit:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load user for editing.',
        open: true,
      });
    }
  };

  useEffect(() => {
    fetchUsers(searchText);
  }, []);


  // Delete functionality functions
  const handleOpenDeleteDialog = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  // Close dialog
  const handleCloseDeleteDialog = () => {
    setSelectedId(null);
    setDeleteDialogOpen(false);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    try {
      const response = await DELETE_DATA('/users', selectedId);
      if (response.status == 500 || response.status == 409 || response.status == 404) {
        setAlert({
          type: 'error',
          message: 'Cannot deleted User.',
          open: true,
        });
      } else {
        setAlert({
          type: 'success',
          message: 'User deleted successfully.',
          open: true,
        });
        await fetchUsers(searchText);
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Failed to delete the user.',
        open: true,
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // Download function
  const handleDownload = async () => {
    try {
      const queryParams = {
        ids: selected.join(','),
      };
      await GET_FILE('/users/download/excel', queryParams, 'xlsx', 'Users');
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const passwordMismatch = formData.password !== formData.confirmPassword;
  const isConfirmValid = formData.confirmPassword?.length > 0 && !passwordMismatch;

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'role',
      headerName: 'User Role',
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ px: 1.5, py: 0.5 }}>
          {getStatusChip(params.value)}
        </Box>
      ),
      sortable: false,
    },
    {
      field: 'active',
      headerName: 'Status',
      sortable: false,
      flex: 1,
      headerAlign: 'center',
      renderCell: (params) => (
        <>
          <Switch
            checked={params.value}
            onChange={async () => {
              try {
                const response = await UPDATE2(`/users/status/${params.id}?active=${!params.value}`);

                if (response?.status === 200 || response?.status === 201) {
                  setAlert({
                    type: 'success',
                    message: `User ${!params.value ? 'activated' : 'deactivated'} successfully!`,
                    open: true,
                  });
                  fetchUsers(searchText);
                } else {
                  setAlert({
                    type: 'error',
                    message: response.data.message,
                    open: true,
                  });
                }
              } catch (error) {
                console.error('Error updating user status:', error);
                setAlert({
                  type: 'error',
                  message: 'Failed to update user status.',
                  open: true,
                });
              }
            }}
            color="success"
          />
          {params.value ? 'Active' : 'Inactive'}
        </>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{ color: 'red' }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(params.id);
              }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const generateStrongPassword = (length = 12) => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()-_=+[]{}|;:,.<>?";
    let password = "";
    for (let i = 0, n = 10; i < length; ++i) {
      password += charset.charAt(Math.floor(Math.random() * n));
    }
    return password;
  };

  const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,100}$/;

  return (
    <Box>
      <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
        All Users
      </Typography>
      <Typography variant="body2" sx={{ color: 'gray', marginBottom: 1 }}>
        Last Updated: {new Date().toLocaleDateString()}
      </Typography>

      <Grid
        container
        spacing={0.5}
        sx={{ mb: 1 }}
        alignItems="center"
        justifyContent="flex-end"
        wrap="nowrap"
      >
        {/* Search Field */}
        <Grid item>
          <TextField
            id="outlined-size-small"
            label="Search"
            variant="outlined"
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                fetchUsers(searchText);
              }
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'gray', mr: 1 }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        </Grid>

        {selected.length > 0 && (
          <Grid item>
            <Button
              variant="contained"
              startIcon={<DownloadOutlined />}
              size="small"
              color="primary"
              onClick={handleDownload}
            >
              Download
            </Button>
          </Grid>
        )}

        <Grid item>
          <Button
            onClick={handleOpenDrawer}
            size="small"
            variant='contained'
            startIcon={<Add />}
          >
            Add User
          </Button>
        </Grid>
      </Grid>

      <Paper
        sx={{
          width: '100%',
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box
            sx={{
              height: 'calc(100vh - 190px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            <Box sx={{ height: 'calc(100vh - 210px)', width: '100%' }}>
              <DataGrid
                rows={users}
                columns={columns}
                checkboxSelection
                hideFooterPagination
                disableRowSelectionOnClick
                sortingOrder={['asc', 'desc']}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0
                    ? 'even-row'
                    : 'odd-row'
                }
                onRowSelectionModelChange={(selectionModel) => {
                  const idsArray = Array.from(selectionModel.ids || []);
                  setSelected(idsArray);
                }}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f2f2f2',
                  },
                  '& .even-row': { backgroundColor: '#f6f6f6' },
                  '& .odd-row': { backgroundColor: '#ffffff' },
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: '0px'
                  },
                  border: 'none',
                }}
              />
            </Box>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px" }}>
              <Pagination
                count={totalPages}
                page={page}
                color="primary"
                onChange={(e, value) => setPage(value)}
                shape="rounded"
              />
            </div>

          </>
        )}
      </Paper>


      <RightDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onSave={handleSaveUser}
        title={editId ? "Edit User" : "Add New User"}
      >
        <Stack spacing={2}>
          {/* User Name */}
          <TextField
            required
            label="User Name"
            name="user_name"
            fullWidth
            size="small"
            value={formData.user_name}
            onChange={handleChange}
            placeholder="Enter user name"
            error={!!formErrors.user_name}
            helperText={formErrors.user_name}
          />

          {
            !editId && (
              <TextField
                required
                label="Email"
                name="email"
                fullWidth
                size="small"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter user email"
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            )
          }

          <FormControl
            required
            fullWidth
            size="small"
            error={!!formErrors.role}
          >
            <InputLabel>User Role</InputLabel>
            <Select
              name="role"
              label="User Role"
              value={formData.role}
              onChange={handleChange}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="vendor">Vendor</MenuItem>
            </Select>
            {formErrors.role && (
              <FormHelperText>{formErrors.role}</FormHelperText>
            )}
          </FormControl>

          {formData.role === 'vendor' && !editId && (
            <Box mt={4}>
              <Accordion sx={{
                backgroundColor: '#f5f5f5',           // light grey background
                border: '1px solid rgba(0,0,0,0.12)',  // subtle border
                borderRadius: 2,                       // theme.spacing(2) ~ 16px
                mb: 1,
                // Remove the default outline on expand
                '&.Mui-expanded': {
                  margin: 0
                },
              }}>
                <AccordionSummary sx={{
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  minHeight: 30,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                  },
                }} expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Vendor Info</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  backgroundColor: '#fff',
                }}>
                  <Stack spacing={2}>
                    <TextField
                      required
                      size="small"
                      label="Vendor Legal Name"
                      value={formData.vendorLegalName}
                      name="vendorLegalName"
                      onChange={handleChange}
                      error={!!errors.vendorLegalName}
                      helperText={errors.vendorLegalName}
                    />
                    <TextField
                      size="small"
                      label="DBA Name (If applicable)"
                      value={formData.dbaName}
                      name="dbaName"
                      onChange={handleChange}
                    />
                    <TextField
                      size="small"
                      label="Business Website"
                      required
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      error={!!errors.website}
                      helperText={errors.website}
                    />
                    <FormControl required size="small" fullWidth error={!!errors.vendorType}>
                      <InputLabel>Vendor Type</InputLabel>
                      <Select
                        value={formData.vendorType}
                        name="vendorType"
                        onChange={handleChange}
                        label="Vendor Type"
                      >
                        <MenuItem value="manufacturer">Manufacturer</MenuItem>
                        <MenuItem value="distributor">Distributor</MenuItem>
                        <MenuItem value="retailer">Retailer</MenuItem>
                      </Select>
                      {errors.vendorType && (
                        <FormHelperText>{errors.vendorType}</FormHelperText>
                      )}
                    </FormControl>
                    <FormControl required size="small" error={!!errors.ownershipType}
                      fullWidth>
                      <InputLabel>Ownership Type</InputLabel>
                      <Select
                        name="ownershipType"
                        value={formData.ownershipType}
                        onChange={handleChange}
                        label="Ownership Type"
                      >
                        <MenuItem value="private">Private</MenuItem>
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="government">Government</MenuItem>
                      </Select>
                      {errors.ownershipType && (
                        <FormHelperText>{errors.ownershipType}</FormHelperText>
                      )}
                    </FormControl>
                    <TextField
                      required
                      size="small"
                      name="taxId"
                      label="Tax ID/VAT Number"
                      value={formData.taxId}
                      onChange={(e) => {
                        const value = e.target.value.trim();
                        handleChange(e);
                        if (value.length < 9) {
                          setErrors((prev) => ({
                            ...prev,
                            taxId: 'Must be at least 9 characters',
                          }));
                        } else {
                          setErrors((prev) => ({ ...prev, taxId: '' }));
                        }
                      }}
                      error={!!errors.taxId}
                      helperText={errors.taxId || `${formData.taxId.length} / min 9 characters`}
                    />

                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{
                backgroundColor: '#f5f5f5',           // light grey background
                border: '1px solid rgba(0,0,0,0.12)',  // subtle border
                borderRadius: 2,                       // theme.spacing(2) ~ 16px
                mb: 1,
                '&.Mui-expanded': {
                  margin: 0
                },
              }}>
                <AccordionSummary sx={{
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  minHeight: 30,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                  },
                }} expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Contact Info</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  backgroundColor: '#fff',
                }}>
                  <Stack spacing={2}>
                    <TextField
                      size="small"
                      label="Contact Name"
                      value={formData.contactFirstName}
                      name="contactFirstName"
                      onChange={handleChange}
                    />
                    <TextField
                      required
                      size="small"
                      label="Contact Email"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      error={!!errors.contactEmail}
                      helperText={errors.contactEmail}
                    />
                    <TextField
                      required
                      size="small"
                      label="Contact Phone"
                      name="contactPhone"
                      type="tel"
                      value={formData.contactPhone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          handleChange(e);
                          if (value.length < 10) {
                            setErrors(prev => ({
                              ...prev,
                              contactPhone: 'Phone number must be 10 digits',
                            }));
                          } else {
                            setErrors(prev => ({ ...prev, contactPhone: '' }));
                          }
                        }
                      }}
                      error={!!errors.contactPhone}
                      helperText={errors.contactPhone}
                    />

                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{
                backgroundColor: '#f5f5f5',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 2,
                mb: 1,
                '&.Mui-expanded': {
                  margin: 0
                },
              }}>
                <AccordionSummary sx={{
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  minHeight: 30,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                  },
                }} expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Address Info</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  backgroundColor: '#fff',
                }}>
                  <Stack spacing={2}>
                    <TextField
                      required
                      size="small"
                      name="zip"
                      label="Zip/Postal Code"
                      value={formData.zip}
                      onChange={handleChange}
                      error={!!errors.zip}
                      helperText={errors.zip}
                    />
                    <TextField
                      required
                      size="small"
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      error={!!errors.country}
                      helperText={errors.country}
                    />
                    <FormControl required size="small" error={!!errors.vendorStatus}
                      fullWidth>
                      <InputLabel>Vendor Status</InputLabel>
                      <Select
                        value={formData.vendorStatus}
                        onChange={handleChange}
                        name="vendorStatus"
                        label="Vendor Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                      {errors.vendorStatus && (
                        <FormHelperText>{errors.vendorStatus}</FormHelperText>
                      )}
                    </FormControl>
                    <TextField
                      multiline
                      rows={1}
                      size="small"
                      name="notes"
                      label="Additional Notes/Comments"
                      value={formData.notes}
                      onChange={handleChange}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          {/* Auto-generate toggle – visible only when not editing */}
          {!editId && !editMode && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPasswordAutoGenerated}
                  onChange={(_, checked) =>
                    setFormData(prev => ({
                      ...prev,
                      isPasswordAutoGenerated: checked,
                      password: checked ? generateStrongPassword() : '',
                      confirmPassword: '',
                    }))
                  }
                  name="isPasswordAutoGenerated"
                  color="success"
                />
              }
              label="Auto-generate password"
            />
          )}
          {/* Show ONLY if not editing AND auto-generate is OFF */}
          {!editId && !editMode && !formData.isPasswordAutoGenerated && (
            <>
              {/* Manual Password Field */}
              <TextField
                required
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                size="small"
                fullWidth
                value={formData.password}
                onChange={e => {
                  const value = e.target.value;
                  setFormData(f => ({ ...f, password: value }));

                  // Real-time validation
                  if (!strongPasswordRegex.test(value)) {
                    setFormErrors(err => ({
                      ...err,
                      password:
                        'Password must be 6–10 characters long, with uppercase, lowercase, number, and special character.',
                    }));
                  } else {
                    setFormErrors(err => ({ ...err, password: '' }));
                  }
                }}
                error={!!formErrors.password}
                helperText={formErrors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{ mr: 1 }}
                        aria-label={showPassword ? 'Hide' : 'Show'}
                        onClick={() => setShowPassword(v => !v)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm Password Field */}
              <TextField
                required
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                size="small"
                fullWidth
                value={formData.confirmPassword}
                onChange={e =>
                  setFormData(f => ({ ...f, confirmPassword: e.target.value }))
                }
                error={passwordMismatch}
                helperText={passwordMismatch ? 'Passwords do not match' : ''}
                FormHelperTextProps={{
                  sx: {
                    color: passwordMismatch ? 'error.main' : 'success.main',
                  },
                }}
                color={isConfirmValid ? 'success' : 'primary'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{ mr: 1 }}
                        aria-label={showConfirm ? 'Hide' : 'Show'}
                        onClick={() => setShowConfirm(v => !v)}
                        edge="end"
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

        </Stack>
      </RightDrawer>


      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete User"
        message="Are you sure you want to delete this user?"
      />

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={alert.type}
          variant="filled"
          onClose={() => setAlert({ ...alert, open: false })}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>


    </Box >
  );
};

export default User;
